import type { AccessLink, BranchDto, EmployeeDto } from '@pos/api-client';
import { ApiError } from '@pos/api-client';
import { formatAmount, parseMoney } from '@pos/business-logic';
import type { PaymentMethod } from '@pos/types';
import {
  AccessLinkPanel,
  Badge,
  BottomSheet,
  Button,
  EmptyState,
  ErrorState,
  Field,
  Input,
  ListGroup,
  ListRow,
  Money,
  Select,
  Skeleton,
  StatusDot,
  Switch,
  Textarea,
  toast,
} from '@pos/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  BarChart3,
  Bell,
  Building2,
  CreditCard,
  ChevronRight,
  Contact,
  LogOut,
  Monitor,
  Plus,
  Store,
  UserCog,
  Users,
  Wallet,
} from 'lucide-react';
import { useState } from 'react';
import { AddRegisterSheet } from './AddRegister';
import { useNavigate } from 'react-router';
import { api, selectBusiness, signOut, useAuth } from '../../app/auth';
import { HeaderButton, Screen } from '../../app/Screen';
import {
  useBranch,
  useBranches,
  useDevices,
  useEmployees,
  useExpenses,
  useNotifications,
  useRoles,
} from '../../app/data';
import { METHOD_LABEL, ago, when } from '../../lib/format';

const chevron = <ChevronRight className="size-5 text-ink-4" />;

export function MorePage() {
  const me = useAuth((s) => s.me);
  const businessId = useAuth((s) => s.businessId);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [pickBusiness, setPickBusiness] = useState(false);
  const current = me?.memberships.find((m) => m.businessId === businessId);
  const unread = useNotifications().data?.unread ?? 0;
  return (
    <Screen title="Ещё">
      {me && (
        <ListGroup>
          <ListRow title={me.user.name} subtitle={me.user.email ?? me.user.phone ?? undefined} />
          {current && (
            <ListRow
              leading={<Building2 className="size-5 text-ink-3" />}
              title={current.businessName}
              subtitle={current.roleName}
              trailing={me.memberships.length > 1 ? chevron : undefined}
              onClick={me.memberships.length > 1 ? () => setPickBusiness(true) : undefined}
            />
          )}
        </ListGroup>
      )}
      <ListGroup>
        <ListRow
          leading={<Bell className="size-5 text-primary" />}
          title="Уведомления"
          trailing={
            <span className="flex items-center gap-2">
              {unread > 0 && <Badge tone="danger">{unread}</Badge>}
              {chevron}
            </span>
          }
          onClick={() => navigate('/more/notifications')}
        />
        <ListRow
          leading={<BarChart3 className="size-5 text-primary" />}
          title="Отчёты"
          trailing={chevron}
          onClick={() => navigate('/more/reports')}
        />
        <ListRow
          leading={<Contact className="size-5 text-primary" />}
          title="Клиенты"
          trailing={chevron}
          onClick={() => navigate('/customers')}
        />
        <ListRow
          leading={<Wallet className="size-5 text-primary" />}
          title="Расходы"
          trailing={chevron}
          onClick={() => navigate('/more/expenses')}
        />
        <ListRow
          leading={<Users className="size-5 text-primary" />}
          title="Сотрудники"
          trailing={chevron}
          onClick={() => navigate('/more/employees')}
        />
        <ListRow
          leading={<Store className="size-5 text-primary" />}
          title="Филиалы"
          trailing={chevron}
          onClick={() => navigate('/more/branches')}
        />
        <ListRow
          leading={<Monitor className="size-5 text-primary" />}
          title="Кассы и устройства"
          trailing={chevron}
          onClick={() => navigate('/more/devices')}
        />
        <ListRow
          leading={<CreditCard className="size-5 text-primary" />}
          title="Подписка"
          trailing={chevron}
          onClick={() => navigate('/more/subscription')}
        />
        <ListRow
          leading={<UserCog className="size-5 text-primary" />}
          title="Профиль и бизнес"
          subtitle="Пароль, название, реквизиты"
          trailing={chevron}
          onClick={() => navigate('/more/profile')}
        />
      </ListGroup>
      <ListGroup>
        <ListRow
          leading={<LogOut className="size-5 text-danger" />}
          title={<span className="text-danger">Выйти</span>}
          onClick={() => void signOut()}
        />
      </ListGroup>
      <p className="px-1 text-center text-xs text-ink-4">Gertsak · владелец</p>
      <BottomSheet open={pickBusiness} onOpenChange={setPickBusiness} title="Бизнес">
        <div className="overflow-hidden rounded-lg border border-line">
          {me?.memberships.map((m) => (
            <ListRow
              key={m.businessId}
              title={m.businessName}
              subtitle={m.roleName}
              trailing={
                m.businessId === businessId ? <Badge tone="primary">Текущий</Badge> : undefined
              }
              onClick={() => {
                selectBusiness(m.businessId);
                useBranch.getState().set(null);
                qc.clear();
                setPickBusiness(false);
                navigate('/home');
              }}
            />
          ))}
        </div>
      </BottomSheet>
    </Screen>
  );
}

// ───────────────────────── Employees ─────────────────────────

const PIN_RE = /^[0-9]{4,8}$/;

/**
 * The team: who sells, with which role, in which branch, with which PIN. A
 * cashier needs only a PIN; a manager can also sign in to this app with
 * Telegram, through a one-time link the owner sends them.
 */
export function EmployeesPage() {
  const { data, isPending, isError, refetch } = useEmployees();
  const branches = useBranches().data ?? [];
  const roles = useRoles().data ?? [];
  const qc = useQueryClient();
  const [edit, setEdit] = useState<EmployeeDto | 'new' | null>(null);
  const [form, setForm] = useState({
    name: '',
    phone: '',
    roleId: '',
    branchId: '',
    pin: '',
    withLogin: false,
    isActive: true,
  });
  const [link, setLink] = useState<(AccessLink & { who: string }) | null>(null);
  const assignable = roles.filter((r) => r.key !== 'OWNER');
  const open = (e: EmployeeDto | 'new') => {
    setEdit(e);
    setForm({
      name: e === 'new' ? '' : e.name,
      phone: e === 'new' ? '' : (e.phone ?? ''),
      roleId:
        e === 'new'
          ? (assignable.find((r) => r.key === 'CASHIER')?.id ?? assignable[0]?.id ?? '')
          : e.role.id,
      branchId: e === 'new' ? '' : (e.branchId ?? ''),
      pin: '',
      withLogin: false,
      isActive: e === 'new' ? true : e.isActive,
    });
  };
  const isNew = edit === 'new';
  const owner = edit !== null && edit !== 'new' && edit.role.key === 'OWNER';
  const save = useMutation({
    mutationFn: () => {
      const base = {
        name: form.name.trim(),
        phone: form.phone.trim() || null,
        branchId: form.branchId || null,
        ...(form.pin ? { pin: form.pin } : {}),
      };
      if (isNew)
        return api.post<EmployeeDto & { accessLink?: AccessLink }>('/employees', {
          ...base,
          roleId: form.roleId,
          ...(form.withLogin ? { cloudAccess: true } : {}),
        });
      const e = edit as EmployeeDto;
      return api.patch<EmployeeDto & { accessLink?: AccessLink }>(`/employees/${e.id}`, {
        ...base,
        ...(owner ? {} : { roleId: form.roleId, isActive: form.isActive }),
      });
    },
    onSuccess: async (res) => {
      await qc.invalidateQueries({ queryKey: ['employees'] });
      toast.success(isNew ? 'Сотрудник добавлен' : 'Сохранено', {
        description: 'Кассы получат изменения при синхронизации',
      });
      setEdit(null);
      if (res.accessLink) setLink({ ...res.accessLink, who: res.name });
    },
    onError: (e) =>
      toast.error('Не сохранено', {
        description:
          e instanceof ApiError && e.code === 'PLAN_LIMIT'
            ? 'Лимит сотрудников тарифа исчерпан — смените тариф'
            : e instanceof ApiError
              ? (Object.values(e.fields)[0] ?? e.message)
              : undefined,
      }),
  });
  const issueLink = useMutation({
    mutationFn: (e: EmployeeDto) => api.post<AccessLink>(`/employees/${e.id}/access-link`),
    onSuccess: async (res, e) => {
      await qc.invalidateQueries({ queryKey: ['employees'] });
      setEdit(null);
      setLink({ ...res, who: e.name });
    },
    onError: (e) =>
      toast.error('Ссылка не создана', {
        description: e instanceof ApiError && !e.isNetwork ? e.message : 'Нет связи с сервером',
      }),
  });
  const set = (k: 'name' | 'phone' | 'pin') => (ev: { target: { value: string } }) =>
    setForm((f) => ({
      ...f,
      [k]: k === 'pin' ? ev.target.value.replace(/\D/g, '').slice(0, 8) : ev.target.value,
    }));
  const pinBad = form.pin !== '' && !PIN_RE.test(form.pin);
  const valid =
    form.name.trim().length >= 2 &&
    form.roleId !== '' &&
    !pinBad &&
    (!isNew || form.pin !== '' || form.withLogin);
  const telegramOf = (e: EmployeeDto) =>
    e.user?.telegramLinkedAt
      ? `Telegram${e.user.telegramUsername ? ` @${e.user.telegramUsername}` : ''}`
      : e.user
        ? 'ждёт входа по ссылке'
        : null;
  const branchName = (id: string | null) =>
    id ? (branches.find((b) => b.id === id)?.name ?? '—') : 'Все филиалы';

  return (
    <Screen
      title="Сотрудники"
      back="/more"
      actions={
        <HeaderButton label="Добавить сотрудника" onClick={() => open('new')}>
          <Plus />
        </HeaderButton>
      }
    >
      {isError ? (
        <ErrorState
          description="Не удалось загрузить сотрудников."
          onRetry={() => void refetch()}
        />
      ) : isPending ? (
        <Skeleton className="h-64 rounded-lg" />
      ) : (data ?? []).length === 0 ? (
        <EmptyState
          icon={<Users />}
          title="Сотрудников нет"
          description="Добавьте кассира: имя, роль и PIN для входа на кассе."
          action={<Button onClick={() => open('new')}>Добавить</Button>}
        />
      ) : (
        <>
          <ListGroup>
            {(data ?? []).map((e) => (
              <ListRow
                key={e.id}
                title={<span className={e.isActive ? undefined : 'text-ink-4'}>{e.name}</span>}
                subtitle={`${e.role.name} · ${branchName(e.branchId)}${e.role.key !== 'OWNER' && telegramOf(e) ? ` · ${telegramOf(e)}` : ''}${e.isActive ? '' : ' · отключён'}`}
                trailing={e.role.key === 'OWNER' ? <Badge tone="primary">Владелец</Badge> : chevron}
                onClick={() => open(e)}
              />
            ))}
          </ListGroup>
          <p className="px-1 text-sm text-ink-3">
            Отключённый сотрудник не сможет войти ни на одной кассе; его продажи и отчёты
            сохраняются.
          </p>
        </>
      )}
      <BottomSheet
        open={edit !== null}
        onOpenChange={(v) => !v && setEdit(null)}
        title={isNew ? 'Новый сотрудник' : form.name || 'Сотрудник'}
        footer={
          <Button
            size="xl"
            variant="primary"
            className="w-full"
            disabled={!valid}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            Сохранить
          </Button>
        }
      >
        <div className="space-y-4">
          <Field label="Имя" required>
            {(a) => (
              <Input {...a} size="lg" value={form.name} onChange={set('name')} autoComplete="off" />
            )}
          </Field>
          <Field label="Телефон">
            {(a) => (
              <Input
                {...a}
                size="lg"
                inputMode="tel"
                value={form.phone}
                onChange={set('phone')}
                placeholder="+998"
              />
            )}
          </Field>
          {!owner && (
            <Field label="Роль" required>
              {(a) => (
                <Select
                  {...a}
                  size="lg"
                  value={form.roleId}
                  onValueChange={(roleId) => setForm((f) => ({ ...f, roleId }))}
                  options={assignable.map((r) => ({ value: r.id, label: r.name }))}
                />
              )}
            </Field>
          )}
          <Field label="Филиал" hint="Где сотрудник может входить на кассу">
            {(a) => (
              <Select
                {...a}
                size="lg"
                value={form.branchId || 'all'}
                onValueChange={(v) => setForm((f) => ({ ...f, branchId: v === 'all' ? '' : v }))}
                options={[
                  { value: 'all', label: 'Все филиалы' },
                  ...branches.map((b) => ({ value: b.id, label: b.name })),
                ]}
              />
            )}
          </Field>
          <Field
            label={isNew ? 'PIN для кассы' : 'Новый PIN'}
            hint={
              isNew
                ? '4–8 цифр. Им сотрудник входит на кассе, в том числе без интернета.'
                : 'Оставьте пустым, чтобы не менять'
            }
            error={pinBad ? 'PIN — от 4 до 8 цифр' : undefined}
          >
            {(a) => (
              <Input
                {...a}
                size="lg"
                type="password"
                inputMode="numeric"
                autoComplete="new-password"
                value={form.pin}
                onChange={set('pin')}
              />
            )}
          </Field>
          {isNew && (
            <div className="rounded-xl border border-line p-4">
              <label className="flex items-center justify-between gap-3">
                <span className="text-md">Вход в приложение владельца</span>
                <Switch
                  checked={form.withLogin}
                  onCheckedChange={(withLogin) => setForm((f) => ({ ...f, withLogin }))}
                  label="Вход в приложение владельца"
                />
              </label>
              <p className="mt-1 text-sm text-ink-3">
                Для управляющего: после сохранения появится ссылка — сотрудник откроет её и войдёт
                через свой Telegram. Кассиру достаточно PIN.
              </p>
            </div>
          )}
          {!isNew && !owner && edit !== null && !(edit as EmployeeDto).user?.telegramLinkedAt && (
            <div className="rounded-xl border border-line p-4">
              <p className="text-md">Вход в приложение владельца</p>
              <p className="mt-1 text-sm text-ink-3">
                {(edit as EmployeeDto).user
                  ? 'Сотрудник ещё не вошёл по ссылке. Если ссылка потерялась или устарела — создайте новую, старая перестанет работать.'
                  : 'Сотрудник входит только на кассу по PIN. Дайте ссылку — и он сможет смотреть отчёты в приложении через Telegram.'}
              </p>
              <Button
                size="lg"
                className="mt-3 w-full"
                loading={issueLink.isPending}
                onClick={() => issueLink.mutate(edit as EmployeeDto)}
              >
                {(edit as EmployeeDto).user
                  ? 'Новая ссылка для входа'
                  : 'Дать доступ через Telegram'}
              </Button>
            </div>
          )}
          {!isNew && !owner && edit !== null && (edit as EmployeeDto).user?.telegramLinkedAt && (
            <p className="rounded-xl border border-line p-4 text-base">
              Входит в приложение через Telegram
              {(edit as EmployeeDto).user?.telegramUsername
                ? ` @${(edit as EmployeeDto).user?.telegramUsername}`
                : ''}
              .
            </p>
          )}
          {!isNew && !owner && (
            <label className="flex items-center justify-between gap-3 rounded-xl border border-line p-4">
              <span>
                <span className="block text-md">Доступ к кассам</span>
                <span className="block text-sm text-ink-3">
                  {form.isActive ? 'Может входить по PIN' : 'Отключён'}
                </span>
              </span>
              <Switch
                checked={form.isActive}
                onCheckedChange={(isActive) => setForm((f) => ({ ...f, isActive }))}
                label="Доступ к кассам"
              />
            </label>
          )}
        </div>
      </BottomSheet>
      <BottomSheet
        open={link !== null}
        onOpenChange={(v) => !v && setLink(null)}
        title={link ? `Ссылка для «${link.who}»` : ''}
      >
        {link && <AccessLinkPanel url={link.url} expiresAt={link.expiresAt} who={link.who} />}
      </BottomSheet>
    </Screen>
  );
}

// ───────────────────────── Branches ─────────────────────────

export function BranchesPage() {
  const { data, isPending, isError, refetch } = useBranches();
  const qc = useQueryClient();
  const [edit, setEdit] = useState<BranchDto | 'new' | null>(null);
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const open = (b: BranchDto | 'new') => {
    setEdit(b);
    setName(b === 'new' ? '' : b.name);
    setAddress(b === 'new' ? '' : (b.address ?? ''));
    setPhone(b === 'new' ? '' : (b.phone ?? ''));
  };
  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: name.trim(),
        address: address.trim() || null,
        phone: phone.trim() || null,
      };
      return edit === 'new'
        ? api.post('/branches', body)
        : api.patch(`/branches/${(edit as BranchDto).id}`, body);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['branches'] });
      toast.success(edit === 'new' ? 'Филиал добавлен' : 'Сохранено');
      setEdit(null);
    },
    onError: (e) =>
      toast.error('Не сохранено', {
        description:
          e instanceof ApiError && e.code === 'PLAN_LIMIT'
            ? 'Лимит филиалов тарифа исчерпан — смените тариф'
            : e instanceof ApiError
              ? e.message
              : undefined,
      }),
  });
  return (
    <Screen
      title="Филиалы"
      back="/more"
      actions={
        <HeaderButton label="Добавить филиал" onClick={() => open('new')}>
          <Plus />
        </HeaderButton>
      }
    >
      {isError ? (
        <ErrorState description="Не удалось загрузить филиалы." onRetry={() => void refetch()} />
      ) : isPending ? (
        <Skeleton className="h-40 rounded-lg" />
      ) : (
        <ListGroup>
          {(data ?? []).map((b) => (
            <ListRow
              key={b.id}
              title={b.name}
              subtitle={[b.address, b.phone].filter(Boolean).join(' · ') || 'Адрес не указан'}
              trailing={chevron}
              onClick={() => open(b)}
            />
          ))}
        </ListGroup>
      )}
      <BottomSheet
        open={edit !== null}
        onOpenChange={(v) => !v && setEdit(null)}
        title={edit === 'new' ? 'Новый филиал' : 'Филиал'}
        footer={
          <Button
            size="xl"
            variant="primary"
            className="w-full"
            disabled={!name.trim()}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            Сохранить
          </Button>
        }
      >
        <div className="space-y-4">
          <Field label="Название" required>
            {(a) => (
              <Input
                {...a}
                size="lg"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Магазин на Чиланзаре"
              />
            )}
          </Field>
          <Field label="Адрес">
            {(a) => (
              <Input
                {...a}
                size="lg"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
            )}
          </Field>
          <Field label="Телефон">
            {(a) => (
              <Input
                {...a}
                size="lg"
                inputMode="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+998"
              />
            )}
          </Field>
        </div>
      </BottomSheet>
    </Screen>
  );
}

// ───────────────────────── Expenses ─────────────────────────

const EXPENSE_CATEGORIES = [
  'Аренда',
  'Зарплата',
  'Коммунальные',
  'Транспорт',
  'Реклама',
  'Налоги',
  'Прочее',
];

export function ExpensesPage() {
  const { data, isPending, isError, refetch } = useExpenses();
  const branches = useBranches().data ?? [];
  const selected = useBranch((s) => s.branchId);
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('Аренда');
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<PaymentMethod>('CASH');
  const [note, setNote] = useState('');
  const [branchId, setBranchId] = useState<string | null>(null);
  const value = parseMoney(amount);
  const add = useMutation({
    mutationFn: () =>
      api.post('/expenses', {
        category,
        amount: value,
        method,
        note: note.trim() || null,
        branchId: branchId ?? selected ?? branches[0]?.id,
      }),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['expenses'] }),
        qc.invalidateQueries({ queryKey: ['dashboard'] }),
      ]);
      toast.success('Расход записан');
      setOpen(false);
      setAmount('');
      setNote('');
    },
    onError: (e) =>
      toast.error('Не записано', { description: e instanceof ApiError ? e.message : undefined }),
  });
  return (
    <Screen
      title="Расходы"
      back="/more"
      actions={
        <HeaderButton label="Добавить расход" onClick={() => setOpen(true)}>
          <Plus />
        </HeaderButton>
      }
    >
      {isError ? (
        <ErrorState description="Не удалось загрузить расходы." onRetry={() => void refetch()} />
      ) : isPending || !data ? (
        <Skeleton className="h-48 rounded-lg" />
      ) : (
        <>
          <div className="mb-4 rounded-2xl border border-line bg-surface p-4">
            <p className="text-sm text-ink-3">За этот месяц</p>
            <Money value={data.total} className="mt-1 block text-4xl font-bold" />
          </div>
          {data.items.length === 0 ? (
            <EmptyState
              icon={<Wallet />}
              title="Расходов нет"
              description="Аренда, зарплата и выплаты из кассы попадут сюда и в прибыль."
            />
          ) : (
            <ListGroup>
              {data.items.map((e) => (
                <ListRow
                  key={`${e.source}-${e.id}`}
                  title={e.note ? `${e.category} · ${e.note}` : e.category}
                  subtitle={`${when(e.at)} · ${METHOD_LABEL[e.method]} · ${e.branchName}`}
                  trailing={<span className="font-medium tnum">−{formatAmount(e.amount)}</span>}
                />
              ))}
            </ListGroup>
          )}
        </>
      )}
      <BottomSheet
        open={open}
        onOpenChange={setOpen}
        title="Новый расход"
        footer={
          <Button
            size="xl"
            variant="primary"
            className="w-full"
            disabled={!value || value <= 0}
            loading={add.isPending}
            onClick={() => add.mutate()}
          >
            Записать
          </Button>
        }
      >
        <div className="space-y-4">
          <Field label="Сумма, сум" required>
            {(a) => (
              <Input
                {...a}
                size="lg"
                inputMode="numeric"
                className="text-2xl font-semibold tnum"
                autoFocus
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            )}
          </Field>
          <Field label="Статья">
            {() => (
              <div className="flex flex-wrap gap-2">
                {EXPENSE_CATEGORIES.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setCategory(c)}
                    aria-pressed={category === c}
                    className={`h-11 rounded-md border px-4 text-md ${category === c ? 'border-primary bg-primary-soft font-medium text-primary-ink' : 'border-line-strong bg-surface active:bg-sunken'}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            )}
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Оплата">
              {(a) => (
                <Select
                  {...a}
                  value={method}
                  onValueChange={(v) => setMethod(v as PaymentMethod)}
                  options={(['CASH', 'CARD', 'CLICK', 'PAYME'] as const).map((m) => ({
                    value: m,
                    label: METHOD_LABEL[m],
                  }))}
                />
              )}
            </Field>
            {branches.length > 1 && (
              <Field label="Филиал">
                {(a) => (
                  <Select
                    {...a}
                    value={branchId ?? selected ?? branches[0]?.id ?? ''}
                    onValueChange={setBranchId}
                    options={branches.map((b) => ({ value: b.id, label: b.name }))}
                  />
                )}
              </Field>
            )}
          </div>
          <Field label="Комментарий">
            {(a) => (
              <Textarea {...a} rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
            )}
          </Field>
          <p className="text-sm text-ink-3">
            Выдачи наличных из кассы кассир записывает сам на кассе — они уже в списке.
          </p>
        </div>
      </BottomSheet>
    </Screen>
  );
}

// ───────────────────────── Devices ─────────────────────────

export function DevicesPage() {
  const { data, isPending, isError, refetch } = useDevices();
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState<{ id: string; name: string } | null>(null);
  const [adding, setAdding] = useState(false);
  const deactivate = useMutation({
    mutationFn: (id: string) => api.post(`/devices/${id}/deactivate`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['devices'] });
      toast.success('Касса отключена', {
        description: 'Она больше не может продавать. Подключить снова — «Добавить кассу»',
      });
      setConfirm(null);
    },
    onError: (e) =>
      toast.error('Не получилось', { description: e instanceof ApiError ? e.message : undefined }),
  });
  const active = (data ?? []).filter((d) => d.status === 'ACTIVE');
  const stale = (iso: string | null) => !iso || Date.now() - new Date(iso).getTime() > 60 * 60_000;
  return (
    <Screen
      title="Кассы и устройства"
      back="/more"
      actions={
        <HeaderButton label="Добавить кассу" onClick={() => setAdding(true)}>
          <Plus />
        </HeaderButton>
      }
    >
      {isError ? (
        <ErrorState description="Не удалось загрузить устройства." onRetry={() => void refetch()} />
      ) : isPending ? (
        <Skeleton className="h-40 rounded-lg" />
      ) : active.length === 0 ? (
        <EmptyState
          icon={<Monitor />}
          title="Касс пока нет"
          description="Установите программу Gertsak на компьютер кассы, затем получите здесь код и введите его на кассе."
          action={<Button onClick={() => setAdding(true)}>Добавить кассу</Button>}
        />
      ) : (
        <>
          <ListGroup>
            {active.map((d) => (
              <ListRow
                key={d.id}
                leading={
                  <StatusDot
                    tone={stale(d.lastSyncAt) ? 'warning' : 'success'}
                    label={
                      <span className="sr-only">
                        {stale(d.lastSyncAt) ? 'Давно не на связи' : 'На связи'}
                      </span>
                    }
                  />
                }
                title={`${d.registerNumber ? `Касса №${d.registerNumber}` : d.name} · ${d.branchName}`}
                subtitle={`${d.lastSyncAt ? `синхронизация ${ago(d.lastSyncAt)}` : 'ещё не синхронизировалась'}${d.appVersion ? ` · v${d.appVersion}` : ''}`}
                trailing={<span className="text-base text-danger">Отключить</span>}
                onClick={() =>
                  setConfirm({
                    id: d.id,
                    name: d.registerNumber ? `Касса №${d.registerNumber}` : d.name,
                  })
                }
              />
            ))}
          </ListGroup>
          <Button size="lg" className="w-full" onClick={() => setAdding(true)}>
            <Plus className="size-5" aria-hidden />
            Добавить кассу
          </Button>
        </>
      )}
      <AddRegisterSheet open={adding} onClose={() => setAdding(false)} />
      <BottomSheet
        open={confirm !== null}
        onOpenChange={(v) => !v && setConfirm(null)}
        title={confirm?.name ?? 'Касса'}
        description="Потеряли, украли или заменили компьютер? Отключите кассу — она сразу перестанет работать."
        footer={
          <Button
            size="xl"
            variant="danger"
            className="w-full"
            loading={deactivate.isPending}
            onClick={() => confirm && deactivate.mutate(confirm.id)}
          >
            Отключить кассу
          </Button>
        }
      >
        <p className="text-md text-ink-3">
          Неотправленные продажи с этой кассы не дойдут до облака. Если касса исправна, сначала
          дождитесь синхронизации.
        </p>
      </BottomSheet>
    </Screen>
  );
}
